import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ConfidenceBadge, StatusPill } from "@/components/badges";
import StationPanel from "@/components/StationPanel";
import { useAuth } from "@/context/AuthContext";
import { ACCESS_TYPES } from "@/lib/connectors";
import { MagnifyingGlassIcon, CrosshairIcon, XIcon, PlusIcon, RoadHorizonIcon } from "@phosphor-icons/react";

function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371, rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(lat2 - lat1), dLng = rad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function Sites() {
  const [sites, setSites] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [label, setLabel] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [access, setAccess] = useState("");
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nearMe, setNearMe] = useState(null); // {lat,lng} or null
  const [radiusKm, setRadiusKm] = useState(5);
  const [locating, setLocating] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const { user } = useAuth();
  const canAdd = user?.role === "admin" || user?.role === "analyst";
  const PAGE = 50;

  useEffect(() => { api.get("/regions").then((r) => setStates(r.data)); }, []);
  useEffect(() => {
    setCity("");
    if (state) api.get("/regions", { params: { state } }).then((r) => setCities(r.data));
    else setCities([]);
  }, [state]);

  const enableNearMe = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported by this browser");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setNearMe({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); toast.success("Showing stations near you"); },
      () => { setLocating(false); toast.error("Could not get your location — allow location access"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const load = () => {
    setLoading(true);
    const f = {};
    if (q) f.q = q;
    if (label) f.label = label;
    if (state) f.state = state;
    if (city) f.city = city;
    if (access) f.access_type = access;
    if (nearMe) {
      const nf = { ...f, lat: nearMe.lat, lng: nearMe.lng, radius_km: radiusKm };
      api.get("/sites", { params: { ...nf, limit: 500 } }).then((r) => {
        const sorted = r.data
          .map((s) => ({ ...s, _dist: distanceKm(nearMe.lat, nearMe.lng, s.latitude, s.longitude) }))
          .sort((a, b) => a._dist - b._dist);
        setSites(sorted); setLoading(false);
      });
      api.get("/sites-count", { params: nf }).then((r) => setTotal(r.data.total));
    } else {
      api.get("/sites", { params: { ...f, limit: PAGE, skip: page * PAGE } }).then((r) => { setSites(r.data); setLoading(false); });
      api.get("/sites-count", { params: f }).then((r) => setTotal(r.data.total));
    }
  };

  useEffect(() => { setPage(0); }, [q, label, state, city, access, nearMe, radiusKm]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, label, state, city, access, page, nearMe, radiusKm]);
  const pages = Math.ceil(total / PAGE) || 1;

  return (
    <div className="p-5 lg:p-8 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-semibold text-slate-900">Sites</h1>
          <p className="text-sm text-slate-500 mt-1">Provenance-stamped charging stations · {total.toLocaleString()} total</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canAdd && (
            <button data-testid="sites-add-station" onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors">
              <PlusIcon size={16} weight="bold" /> Add Station
            </button>
          )}
          <div className="relative">
            <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="site-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name…"
              className="rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100" />
          </div>
          <select data-testid="site-filter-state" value={state} onChange={(e) => setState(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm outline-none focus:border-emerald-500 max-w-[180px]">
            <option value="">All states</option>
            {states.map((s) => <option key={s.name} value={s.name}>{s.name} ({s.count.toLocaleString()})</option>)}
          </select>
          <select data-testid="site-filter-city" value={city} onChange={(e) => setCity(e.target.value)} disabled={!state}
            className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm outline-none focus:border-emerald-500 max-w-[180px] disabled:opacity-50">
            <option value="">{state ? "All cities" : "Select state first"}</option>
            {cities.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count.toLocaleString()})</option>)}
          </select>
          <select data-testid="site-filter-label" value={label} onChange={(e) => setLabel(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm outline-none focus:border-emerald-500">
            <option value="">All confidence</option>
            <option value="Verified">Verified</option>
            <option value="Unverified">Unverified</option>
            <option value="Stale">Stale</option>
          </select>
          <select data-testid="site-filter-access" value={access} onChange={(e) => setAccess(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm outline-none focus:border-emerald-500">
            <option value="">All access</option>
            {ACCESS_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          {nearMe ? (
            <div className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 py-1 pl-2.5 pr-1">
              <CrosshairIcon size={16} weight="fill" className="text-emerald-600" />
              <select data-testid="near-radius" value={radiusKm} onChange={(e) => setRadiusKm(Number(e.target.value))}
                className="bg-transparent text-sm text-emerald-700 outline-none">
                {[2, 5, 10, 25, 50].map((r) => <option key={r} value={r}>within {r} km</option>)}
              </select>
              <button data-testid="near-me-clear" onClick={() => setNearMe(null)} className="rounded p-1 text-emerald-600 hover:bg-emerald-100" title="Clear near me"><XIcon size={14} /></button>
            </div>
          ) : (
            <button data-testid="near-me-btn" onClick={enableNearMe} disabled={locating}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-60">
              <CrosshairIcon size={16} weight="fill" /> {locating ? "Locating…" : "Near me"}
            </button>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-2.5 font-semibold">Site</th>
                {nearMe && <th className="px-4 py-2.5 font-semibold">Distance</th>}
                <th className="px-4 py-2.5 font-semibold">Operator</th>
                <th className="px-4 py-2.5 font-semibold">Power</th>
                <th className="px-4 py-2.5 font-semibold">Tariff ₹/kWh</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Access</th>
                <th className="px-4 py-2.5 font-semibold">Highway</th>
                <th className="px-4 py-2.5 font-semibold">Confidence</th>
                <th className="px-4 py-2.5 font-semibold">Refreshed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sites.map((s) => (
                <tr key={s.id} data-testid={`site-row-${s.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/sites/${s.id}`} className="font-medium text-slate-900 hover:text-emerald-600">{s.name}</Link>
                    <div className="text-xs text-slate-400">{s.address}</div>
                  </td>
                  {nearMe && <td className="px-4 py-3 font-mono text-emerald-700 whitespace-nowrap">{s._dist != null ? `${s._dist.toFixed(1)} km` : "—"}</td>}
                  <td className="px-4 py-3 text-slate-600">{s.operator || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 font-mono text-slate-600">{s.power_kw ? `${s.power_kw} kW` : "—"}</td>
                  <td className="px-4 py-3 font-mono text-slate-600">{s.tariff_per_kwh ?? "—"}</td>
                  <td className="px-4 py-3"><StatusPill status={s.status} /></td>
                  <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{s.access_type || "Public"}</span></td>
                  <td className="px-4 py-3">
                    {s.enrichment?.highway_proximity_status ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 px-2 py-0.5 text-xs font-medium text-sky-700" title={s.enrichment.highway_proximity_status}>
                        <RoadHorizonIcon size={12} weight="bold" /> {s.enrichment.highway_proximity_status.startsWith("On") ? "On highway" : "Near highway"}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3"><ConfidenceBadge label={s.confidence?.label} score={s.confidence?.overall_score} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{s.last_refreshed_at ? new Date(s.last_refreshed_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {!loading && sites.length === 0 && (
                <tr><td colSpan={nearMe ? 10 : 9} className="px-4 py-10 text-center text-slate-400">{nearMe ? "No stations within this radius." : "No sites match your filters."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {nearMe ? (
        <div className="text-sm text-slate-500">
          {total.toLocaleString()} station{total === 1 ? "" : "s"} within {radiusKm} km · showing nearest {sites.length}
        </div>
      ) : (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">
            {total === 0 ? "0 results" : `Showing ${page * PAGE + 1}–${Math.min((page + 1) * PAGE, total)} of ${total.toLocaleString()}`}
          </span>
          <div className="flex items-center gap-2">
            <button data-testid="sites-prev" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors">Previous</button>
            <span className="font-mono text-xs text-slate-400">Page {page + 1} / {pages}</span>
            <button data-testid="sites-next" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors">Next</button>
          </div>
        </div>
      )}

      <StationPanel open={addOpen} mode="create" onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />
    </div>
  );
}
